import { useMount, useUpdateEffect } from 'ahooks';
import React, { PropsWithChildren, useRef } from 'react';
import { ScrollArea } from '@ibsys/kit';
import { cn, scrollIntoViewWithinContainer } from '@/shared/lib';

type ChatHistoryProps = {
  autoDownScroll?: boolean;
  className?: string;
  classNames?: {
    content?: string;
  };
};

/**
 * Базовый компонент для отображения истории чата
 * Обрабатывает только скролл и отображение children
 * Логика группировки и обработки сообщений должна быть в местах использования
 */
function ChatNotMemo({ children, autoDownScroll = true, className, classNames }: PropsWithChildren<ChatHistoryProps>) {
  const historyRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);

  // Автопрокрутка вниз
  useMount(() => {
    if (autoDownScroll) {
      scrollIntoViewWithinContainer(messagesEndRef.current, { behavior: 'instant' });
    }
  });

  // Batch rapid scroll updates (e.g. during streaming) into a single frame
  useUpdateEffect(() => {
    if (!autoDownScroll) return;

    const rafId = requestAnimationFrame(() => {
      scrollIntoViewWithinContainer(messagesEndRef.current, { behavior: 'instant' });
    });

    return () => cancelAnimationFrame(rafId);
  }, [children, autoDownScroll]);

  return (
    <div className={cn('h-full flex flex-col justify-end relative', className)}>
      <ScrollArea aria-orientation="vertical" ref={historyRef}>
        <div className={cn('flex h-full flex-col gap-3 relative w-full p-1 pr-3', classNames?.content)}>{children}</div>
        <div ref={messagesEndRef} />
      </ScrollArea>
    </div>
  );
}

export const Chat = React.memo(ChatNotMemo);
